import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-js';
import { getConfig } from '../config';
import { getCredentials, refreshCredentials, getJwtTenantId } from './auth';

// Re-export for consumers that import getCredentials from client
export { getCredentials } from './auth';

export async function signedFetch(method: string, path: string, body?: unknown): Promise<{ status: number; data: unknown }> {
  return doSignedFetch(method, path, body, false);
}

async function doSignedFetch(method: string, path: string, body: unknown, isRetry: boolean): Promise<{ status: number; data: unknown }> {
  let credentials = getCredentials();
  if (!credentials && !isRetry) {
    const refreshed = await refreshCredentials();
    if (refreshed) credentials = getCredentials();
  }
  if (!credentials) {
    throw new Error('Not authenticated. Credentials unavailable.');
  }

  const config = getConfig();
  const baseUrl = config.apiUrl.replace(/\/+$/, '');
  const url = new URL(`${baseUrl}${path}`);

  const headers: Record<string, string> = { host: url.hostname };
  if (body) {
    headers['Content-Type'] = 'application/json';
  }

  // Tenant: derived from custom:tenant_id in the user's JWT (set during login).
  // No URL param override — tenant is always controlled by the authenticated identity.
  const tenantId = getJwtTenantId();
  if (tenantId) {
    headers['X-Tenant-Id'] = tenantId;
  }

  const query: Record<string, string> = {};
  url.searchParams.forEach((v, k) => { query[k] = v; });

  const request = new HttpRequest({
    method,
    protocol: url.protocol,
    hostname: url.hostname,
    path: url.pathname,
    query,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const signer = new SignatureV4({
    service: 'execute-api',
    region: config.region,
    credentials,
    sha256: Sha256,
  });

  const signed = await signer.sign(request);

  const response = await fetch(url.toString(), {
    method,
    headers: signed.headers as Record<string, string>,
    body: body ? JSON.stringify(body) : undefined,
  });

  // Auto-refresh on 403 (expired Identity Pool creds) — one retry only
  if (response.status === 403 && !isRetry) {
    const refreshed = await refreshCredentials();
    if (refreshed) {
      return doSignedFetch(method, path, body, true);
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errObj = errorData as { error?: string; message?: string; position?: number } | null;
    const pos = errObj?.position != null ? ` (at character ${errObj.position})` : '';
    const prefix = errObj?.error ? `${errObj.error}: ` : '';
    throw new Error(
      `${prefix}${errObj?.message || `Request failed with status ${response.status}`}${pos}`,
    );
  }

  const data = await response.json().catch(() => null);
  return { status: response.status, data };
}
